import { useCallback, useMemo, useState, useEffect } from 'react'
import styled from '@emotion/styled'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import FormControl from '@mui/material/FormControl'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Popover from '@mui/material/Popover'
import TextField from '@mui/material/TextField'
import { ComparisonOperatorSelect } from '@components'
import { FIELD_ID_ALL } from '@datasets/dataset-query-context'
import { useDatasetQueryContext } from '@datasets/dataset-query-context/hooks'
import { QueryOptionFilter } from '@datasets/dataset-query-context/utils/queryOptions'
import { canHaveValueOperand, comparisonOperatorConfigs } from '@global/constants'
import { GenericSelect } from '../../../components/select/GenericSelect'
import { t } from '@lingui/core/macro'
import { i18n } from '@lingui/core'

const MAX_FILTER_COUNT = 5

const ButtonListItem = styled(ListItem)`
  justify-content: flex-end;

  > :nth-of-type(n + 2) {
    margin-left: ${props => props.theme.spacing(1)};
  }
`

const ComparisonOperatorFormControl = styled(FormControl)`
  width: 340px;
`

const FilterListItem = styled(ListItem)`
  align-items: flex-end;

  > :nth-of-type(n + 2) {
    margin-left: ${props => props.theme.spacing(1)};
  }
`

const FilterListItemButtons = styled.div`
  width: 110px;

  > :nth-of-type(n + 2) {
    margin-left: 2px;
  }
`

const FilterListItemIcon = styled(ListItemIcon)`
  min-width: 30px;
`

const PopoverContent = styled.div`
  width: 400px;
`

const SmallIconButton = styled(IconButton)`
  padding: 2px;
`

const ComparisonOperatorValueField = ({ filterIndex, filterValue, onChange, operator }) => {
  const operatorConfig = comparisonOperatorConfigs[operator]
  const hasData = operatorConfig?.data

  const getOperatorItems = useCallback(
    config =>
      Object.entries(config.data).map(([key, value]) => ({
        value: key,
        text: value,
      })),
    [],
  )

  return hasData ? (
    <GenericSelect
      label={i18n._(operatorConfig.dataLabel)}
      name="value"
      value={filterValue}
      items={getOperatorItems(operatorConfig)}
      onChange={onChange(filterIndex)}
      noneOption={operatorConfig.noneOption}
      noneOptionLabel={i18n._(operatorConfig.noneOptionLabel)}
      required={!operatorConfig.noneOption}
      data-testid="generic-select"
      fullWidth
    />
  ) : (
    <TextField
      label={t`Value`}
      name="value"
      value={filterValue}
      onChange={onChange(filterIndex)}
      disabled={!operatorConfig?.canHaveValueOperand()}
      required={operatorConfig?.canHaveValueOperand()}
      data-testid="text-field"
      InputProps={{
        inputProps: {
          'data-testid': 'inner-input-field',
        },
      }}
      fullWidth
    />
  )
}

export const GlobalFilterPopover = ({ anchorEl, onClose, open }) => {
  const fieldId = FIELD_ID_ALL
  const { getFiltersByFieldId, updateFiltersByFieldId } = useDatasetQueryContext()

  const [filters, setFilters] = useState(getFiltersByFieldId(fieldId))

  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])

  const handleFilterOperatorChange = useCallback(
    filterIndex => event => {
      const newOperator = event.target.value
      setFilters(oldFilters => {
        const newFilters = [...oldFilters]
        const newFilter = {
          ...newFilters[filterIndex],
          operator: newOperator,
        }

        if (newOperator && !canHaveValueOperand(newOperator)) {
          newFilter.value = ''
        }

        newFilters[filterIndex] = newFilter
        return newFilters
      })
    },
    [],
  )

  const handleFilterValueChange = useCallback(
    filterIndex => event => {
      const newValue = event.target.value
      setFilters(oldFilters => {
        const newFilters = [...oldFilters]
        newFilters[filterIndex] = {
          ...newFilters[filterIndex],
          value: newValue,
        }
        return newFilters
      })
    },
    [],
  )

  const addFilter = useCallback(
    filterIndex => () => {
      if (filters.length === MAX_FILTER_COUNT) {
        return
      }

      setFilters(oldFilters => {
        const newFilter = new QueryOptionFilter(fieldId)
        const firstHalf = oldFilters.slice(0, filterIndex)
        const lastHalf = oldFilters.slice(filterIndex)
        return [...firstHalf, newFilter, ...lastHalf]
      })
    },
    [filters],
  )

  const removeFilter = useCallback(
    filterIndex => () => {
      setFilters(oldFilters => oldFilters.filter((filter, index) => index !== filterIndex))
    },
    [],
  )

  const clearColumnQueryOptions = useCallback(() => {
    if (filters.length === 0) {
      return
    }

    setFilters([])
    updateFiltersByFieldId(fieldId, [])
  }, [filters, fieldId])

  const resetColumnQueryOptions = useCallback(() => {
    setFilters(getFiltersByFieldId(fieldId))
  }, [fieldId, getFiltersByFieldId])

  const applyColumnQueryOptions = useCallback(() => {
    onClose()
    updateFiltersByFieldId(fieldId, filters)
  }, [fieldId, filters, onClose, updateFiltersByFieldId])

  const anchorOrigin = useMemo(
    () => ({
      vertical: 'bottom',
      horizontal: 'right',
    }),
    [],
  )

  const transformOrigin = useMemo(
    () => ({
      vertical: 'top',
      horizontal: 'right',
    }),
    [],
  )

  useEffect(() => {
    resetColumnQueryOptions()
  }, [open, resetColumnQueryOptions])

  return (
    <Popover
      anchorEl={anchorEl}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      open={open}
      onClose={handleClose}
      data-testid="filter-popover"
    >
      <PopoverContent data-testid="filter-popover-content">
        <List aria-label="Global filters" data-testid="filter-popover-list">
          {filters.length > 0 ? (
            filters.map((filter, index) => (
              <FilterListItem key={filter.id} data-testid="filter-list-item" dense>
                <ComparisonOperatorSelect
                  value={filter.operator}
                  onChangeSelect={handleFilterOperatorChange(index)}
                  operatorFilter={operatorConfig => operatorConfig.nullary}
                  FormControlComponent={ComparisonOperatorFormControl}
                  size="small"
                  required
                />
                <ComparisonOperatorValueField
                  filterIndex={index}
                  filterValue={filter.value}
                  operator={filter.operator}
                  onChange={handleFilterValueChange}
                />
                <FilterListItemButtons data-testid="filter-list-buttons-wrapper">
                  <SmallIconButton onClick={removeFilter(index)}>
                    <RemoveIcon fontSize="small" data-testid="remove-icon-small" />
                  </SmallIconButton>
                  <SmallIconButton
                    onClick={addFilter(index + 1)}
                    disabled={filters.length === MAX_FILTER_COUNT}
                  >
                    <AddIcon fontSize="small" data-testid="add-icon-small" />
                  </SmallIconButton>
                </FilterListItemButtons>
              </FilterListItem>
            ))
          ) : (
            <ListItem button onClick={addFilter(0)} data-testid="filter-list-item">
              <FilterListItemIcon>
                <AddIcon data-testid="add-icon" />
              </FilterListItemIcon>
              <ListItemText primary="Add Filter" />
            </ListItem>
          )}
        </List>
        <Divider />
        <List>
          <ButtonListItem data-testid="button-wrapper">
            <Button onClick={clearColumnQueryOptions} data-testid="button-reset-filter">
              {t`Reset`}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={applyColumnQueryOptions}
              data-testid="button-apply-filter"
            >
              {t`Apply`}
            </Button>
          </ButtonListItem>
        </List>
      </PopoverContent>
    </Popover>
  )
}
